import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CONTEXT_CATEGORIES,
  buildContextSnapshot,
  formatTokenCount,
} from "./contextUsage";

function fillTone(pct) {
  if (pct >= 90) return { bar: "bg-red-500", text: "text-red-700", chip: "border-red-200 bg-red-50" };
  if (pct >= 75) return { bar: "bg-amber-500", text: "text-amber-800", chip: "border-amber-200 bg-amber-50" };
  if (pct >= 50) return { bar: "bg-indigo-500", text: "text-indigo-700", chip: "border-indigo-200 bg-indigo-50" };
  return { bar: "bg-slate-500", text: "text-slate-700", chip: "border-slate-200 bg-slate-50" };
}

function StackedBar({ categories, windowSize, height = 8 }) {
  const free = Math.max(
    0,
    windowSize - CONTEXT_CATEGORIES.reduce((s, c) => s + (categories[c.id] || 0), 0)
  );
  return (
    <div
      className="flex w-full overflow-hidden rounded-full bg-slate-100"
      style={{ height }}
      role="img"
      aria-label="Context composition"
    >
      {CONTEXT_CATEGORIES.map((c) => {
        const tokens = categories[c.id] || 0;
        if (tokens <= 0 || windowSize <= 0) return null;
        const widthPct = Math.max(0.4, (tokens / windowSize) * 100);
        return (
          <div
            key={c.id}
            title={`${c.label}: ${formatTokenCount(tokens)}`}
            style={{ width: `${widthPct}%`, backgroundColor: c.color }}
            className="h-full first:rounded-l-full"
          />
        );
      })}
      {free > 0 && (
        <div
          className="h-full flex-1 bg-slate-100"
          title={`Free: ${formatTokenCount(free)}`}
        />
      )}
    </div>
  );
}

function formatUsd(n) {
  if (n == null || Number.isNaN(n)) return null;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(3)}`;
}

function ContextTooltip({ snapshot, title = "Context" }) {
  const { categories, promptTokens, windowSize, pct, cache, cacheHitPct, costUsd, measured, compressed, engine } =
    snapshot;
  const free = Math.max(0, windowSize - promptTokens);

  return (
    <div className="w-72 rounded-xl border border-slate-200 bg-white p-3.5 shadow-xl shadow-slate-900/10 ring-1 ring-black/5">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {title}
        </div>
        <div className="text-[11px] font-mono text-slate-600">
          <span className="font-semibold text-slate-900">
            {formatTokenCount(promptTokens)}
          </span>
          <span className="text-slate-400"> / {formatTokenCount(windowSize)}</span>
          <span className="ml-1.5 text-slate-500">{pct.toFixed(0)}%</span>
        </div>
      </div>

      <StackedBar categories={categories} windowSize={windowSize} height={10} />

      <ul className="mt-3 space-y-1.5">
        {CONTEXT_CATEGORIES.map((c) => {
          const tokens = categories[c.id] || 0;
          if (tokens <= 0) return null;
          const share = promptTokens > 0 ? (tokens / promptTokens) * 100 : 0;
          return (
            <li key={c.id} className="flex items-center gap-2 text-[11px]">
              <span
                className="h-2 w-2 shrink-0 rounded-sm"
                style={{ backgroundColor: c.color }}
              />
              <span className="flex-1 text-slate-600 truncate" title={c.hint}>
                {c.label}
              </span>
              <span className="font-mono text-slate-800 tabular-nums">
                {formatTokenCount(tokens)}
              </span>
              <span className="w-8 text-right font-mono text-slate-400 tabular-nums">
                {share.toFixed(0)}%
              </span>
            </li>
          );
        })}
        <li className="flex items-center gap-2 text-[11px] pt-0.5 border-t border-slate-100 mt-1.5">
          <span className="h-2 w-2 shrink-0 rounded-sm bg-slate-200" />
          <span className="flex-1 text-slate-500">Free</span>
          <span className="font-mono text-slate-500 tabular-nums">
            {formatTokenCount(free)}
          </span>
          <span className="w-8 text-right font-mono text-slate-400 tabular-nums">
            {((free / windowSize) * 100).toFixed(0)}%
          </span>
        </li>
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500 border-t border-slate-100 pt-2.5">
        {cache.read > 0 && (
          <span title="Tokens served from prompt cache">
            Cache hit{" "}
            <span className="font-mono font-medium text-teal-700">
              {cacheHitPct.toFixed(0)}%
            </span>
            <span className="text-slate-400">
              {" "}
              · {formatTokenCount(cache.read)} read
            </span>
          </span>
        )}
        {cache.write > 0 && (
          <span title="Tokens written into the prompt cache this turn">
            Cache write{" "}
            <span className="font-mono font-medium text-slate-700">
              {formatTokenCount(cache.write)}
            </span>
          </span>
        )}
        {costUsd != null && (
          <span className="font-mono font-medium text-violet-700">
            {formatUsd(costUsd)}
          </span>
        )}
        <span
          className="text-slate-400 ml-auto"
          title={
            measured
              ? `Measured from the request payload${engine ? ` (${engine})` : ""}${
                  compressed ? "; context was compressed" : ""
                }`
              : "Category split estimated from the timeline (legacy run)"
          }
        >
          {measured ? "measured" : "est."}
          {compressed ? " · compressed" : ""}
        </span>
      </div>
    </div>
  );
}

/**
 * Compact context meter (Cursor / Claude Code style).
 * Shows fill % with a mini bar; hover/focus reveals category breakdown.
 *
 * @param {boolean} [portal] - render tooltip via portal (needed inside overflow-hidden headers)
 * @param {string} [title] - tooltip heading (e.g. "Final context")
 */
export default function ContextMeter({
  usage,
  events,
  eventIndex,
  llmMeta,
  contextWindow,
  className = "",
  portal = false,
  title = "Context",
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const tipId = useId();
  const anchorRef = useRef(null);

  const snapshot = buildContextSnapshot({
    usage: usage || llmMeta?.usage,
    events,
    eventIndex,
    contextWindow,
    llmMeta,
  });

  useLayoutEffect(() => {
    if (!open || !portal || !anchorRef.current) return undefined;
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const tipWidth = 288; // w-72
      const left = Math.min(
        Math.max(8, rect.right - tipWidth),
        window.innerWidth - tipWidth - 8
      );
      const spaceBelow = window.innerHeight - rect.bottom;
      const placeAbove = spaceBelow < 280 && rect.top > spaceBelow;
      setCoords({
        left,
        top: placeAbove ? undefined : rect.bottom + 8,
        bottom: placeAbove ? window.innerHeight - rect.top + 8 : undefined,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, portal]);

  useEffect(() => {
    if (!open) setCoords(null);
  }, [open]);

  if (!snapshot) return null;

  const tone = fillTone(snapshot.pct);

  const tooltip = open && (
    <div
      id={tipId}
      role="tooltip"
      className={
        portal
          ? "fixed z-[100] pointer-events-none"
          : "absolute z-50 top-full right-0 mt-2 pointer-events-none"
      }
      style={
        portal && coords
          ? { left: coords.left, top: coords.top, bottom: coords.bottom }
          : undefined
      }
    >
      <ContextTooltip snapshot={snapshot} title={title} />
    </div>
  );

  return (
    <div
      ref={anchorRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        role="button"
        tabIndex={0}
        aria-describedby={open ? tipId : undefined}
        aria-label={`${title}: ${formatTokenCount(snapshot.promptTokens)} of ${formatTokenCount(snapshot.windowSize)} (${snapshot.pct.toFixed(0)}%)${snapshot.measured ? ", measured" : ", estimated"}`}
        className={`group/ctx inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 cursor-default ${tone.chip} ${tone.text}`}
      >
        <span
          className="relative h-1.5 w-10 overflow-hidden rounded-full bg-white/80 border border-black/5"
          aria-hidden
        >
          <span
            className={`absolute inset-y-0 left-0 rounded-full ${tone.bar}`}
            style={{ width: `${Math.max(2, Math.min(100, snapshot.pct))}%` }}
          />
        </span>
        <span className="font-mono tabular-nums">
          {formatTokenCount(snapshot.promptTokens)}
          <span className="opacity-60">/{formatTokenCount(snapshot.windowSize)}</span>
        </span>
        <span className="tabular-nums opacity-80">{snapshot.pct.toFixed(0)}%</span>
      </div>

      {portal
        ? open && coords && typeof document !== "undefined"
          ? createPortal(tooltip, document.body)
          : null
        : tooltip}
    </div>
  );
}

/**
 * Richer card used on standalone llm_call rows (when system events are shown).
 */
export function ContextUsageCard({ usage, events, eventIndex, llmMeta, contextWindow }) {
  const snapshot = buildContextSnapshot({
    usage: usage || llmMeta?.usage,
    events,
    eventIndex,
    contextWindow,
    llmMeta,
  });
  if (!snapshot) return null;

  const tone = fillTone(snapshot.pct);

  return (
    <div className={`rounded-xl border p-3 max-w-md ${tone.chip}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Context window
          <span className="ml-1.5 font-medium normal-case tracking-normal text-slate-400">
            {snapshot.measured ? "measured" : "est."}
          </span>
        </div>
        <div className={`text-xs font-mono font-semibold ${tone.text}`}>
          {formatTokenCount(snapshot.promptTokens)} / {formatTokenCount(snapshot.windowSize)}{" "}
          <span className="opacity-70">({snapshot.pct.toFixed(0)}%)</span>
        </div>
      </div>
      <StackedBar categories={snapshot.categories} windowSize={snapshot.windowSize} height={8} />
      <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1">
        {CONTEXT_CATEGORIES.map((c) => {
          const tokens = snapshot.categories[c.id] || 0;
          if (tokens <= 0) return null;
          return (
            <div key={c.id} className="flex items-center gap-1.5 text-[10px] text-slate-600">
              <span className="h-1.5 w-1.5 rounded-sm" style={{ backgroundColor: c.color }} />
              <span className="truncate">{c.label}</span>
              <span className="ml-auto font-mono tabular-nums text-slate-800">
                {formatTokenCount(tokens)}
              </span>
            </div>
          );
        })}
      </div>
      {(snapshot.cache.read > 0 || snapshot.costUsd != null) && (
        <div className="mt-2 flex flex-wrap gap-x-3 text-[10px] text-slate-500 border-t border-black/5 pt-2">
          {snapshot.cache.read > 0 && (
            <span>
              Cache hit{" "}
              <span className="font-mono font-medium text-teal-700">
                {snapshot.cacheHitPct.toFixed(0)}%
              </span>
            </span>
          )}
          {snapshot.costUsd != null && (
            <span className="font-mono font-medium text-violet-700">
              {formatUsd(snapshot.costUsd)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
